<style lang="less" scoped>
  @import "../../../../style/theme.less";

</style>

<template>
  <div>
    <a-row type="flex" :gutter="[16,16]">
      <a-col flex="auto">
        <div class="ant-descriptions-title">Report Blocks</div>
      </a-col>
      <a-col flex="60px">
        <a-button
          type="primary"
          shape="circle"
          icon="plus"
          @click="() => setShowCreateReportBlockModal(true)"
        />
      </a-col>
    </a-row>

    <a-table
      :columns="columns"
      :data-source="formattedReportBlocks"
      :pagination="false"
      :rowKey="({ id }) => id"
      :rowClassName="({ id }) => id === reportBlockEditId ? 'highlighted' : ''"
    >

      <span slot="actions" slot-scope="record">
        <a-button
          type="primary"
          shape="circle"
          icon="edit"
          @click="() => $emit('edit', record.id)"
        />

        <a-divider type="vertical" />

        <a-popconfirm
          title="Are you sure you want to delete this report block?"
          @confirm="() => removeReportBlock(record.id)"
        >
          <a-button type="primary" shape="circle" icon="delete"/>
        </a-popconfirm>
      </span>

    </a-table>

    <create-report-block-modal />
  </div>
</template>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import createReportBlockModal from './_create-report-block-modal';

const columns = [
  {
    title: 'Index',
    dataIndex: 'index',
    width: '100px',
    align: 'center',
    customRender: (index, row) => ({
      children: index,
      attrs: { rowSpan: row.indexCount || 0, },
    }),
  }, {
    title: 'Conditions',
    dataIndex: 'conditions',
  }, {
    title: 'Result',
    dataIndex: 'descriptor',
  }, {
    width: '160px',
    align: 'right',
    scopedSlots: { customRender: 'actions', },
  },
];
export default {
  name: 'admin.report-types.report-blocks',

  data: () => ({
    columns,
  }),

  props: ['reportBlocks',],

  components: {
    createReportBlockModal,
  },

  mounted() {
    return Promise.all([
      this.listPersonalityTraits(),
      this.listConstants(),
    ]);
  },

  computed: {
    ...mapState('admin', ['reportBlockEditId', 'constants',]),

    formattedReportBlocks() {
      if (!this.reportBlocks) {
        return [];
      }

      return this.reportBlocks.map(({ id, index, condition: { conditions, }, descriptor, }, mapIndex, blocks) => {
        const blockConditions = Object.keys(conditions).reduce((acc, key) => {
          const condition = conditions[key];

          const formattedPartials = Object.keys(condition).map(operator => this.formatConditionPartial({
            key,
            condition: condition[operator],
            operator,
          }));

          let result = formattedPartials.filter(e => !!e).map(e => `${e.field} ${e.condition} ${e.value}`);

          acc.push(result.join(' and '));

          return acc;
        }, []);

        const indexValues = {};
        if (mapIndex === 0 || blocks[mapIndex - 1].index !== index) {
          indexValues.index = index + 1;
          indexValues.indexCount = blocks.filter(({ index: blockIndex, }) => blockIndex === index).length;
        }

        return {
          id,
          descriptor,
          conditions: blockConditions.length ? blockConditions.join(', ') : 'none',
          ...indexValues,
        };
      });
    },

    conditionTypes() {
      const types = this.reportBlocks.reduce((acc, { condition: { conditions, }, }) => {
        const types = Object.keys(conditions).map(key => Object.keys(conditions[key])).flat();

        acc.add(types);

        return acc;
      }, new Set());

      return [...types,];
    },
  },

  methods: {
    ...mapActions('admin', ['listPersonalityTraits', 'removeReportBlock', 'listConstants',]),
    ...mapGetters('admin', ['resolvePersonalityTrait',]),
    ...mapMutations('admin', ['setShowCreateReportBlockModal',]),

    formatConditionPartial({ key, condition, operator, }) {
      const { conditions: { typeDescriptors, operatorDescriptorsShort, types, operators, }, genders: { genderDescriptors, }, } = this.constants;
      if (!condition || !Object.keys(operators).includes(operator)) {
        return null;
      }

      const inputDescriptor = typeDescriptors[key] || this.resolvePersonalityTrait()(key, true).descriptor;

      const { type, value, correction = {}, inclusive, } = condition;

      const operatorDescriptor = `${operatorDescriptorsShort[operator]}${inclusive === false ? '' : '='}`;

      const valueDescriptor = {
        [types.literal]: genderDescriptors[value] || value,
        [types.personalityTrait]: this.resolvePersonalityTrait()(value, true).descriptor,
      }[type] || typeDescriptors[type];

      const correctionDescriptor = Object.keys(correction).reduce((acc, correctionType) => {
        if (correctionType === 'add') {
          return `${acc} + ${correction.add}`.trim();
        } else if (correctionType === 'subtract') {
          return `${acc} - ${correction.subtract}`.trim();
        }

        return acc;
      }, '');

      return {
        field: inputDescriptor,
        condition: operatorDescriptor,
        value: valueDescriptor + correctionDescriptor,
      };
    },
  },
};
</script>
